import * as Yup from 'yup';

import { requiredString } from './common';

const APPLICATION_TYPES = ['CONSUMER', 'MERCHANT'];

/** Digits with optional dotted segments: 1, 1.2, 1.2.3.5 */
const VERSION_COMPLETE_PATTERN = /^\d+(\.\d+)*$/;

const hasText = (value) => String(value ?? '').trim() !== '';

export const hasIosPlatformData = (values = {}) =>
  [values.iosVersionName, values.iosBuildVersion, values.iosMinForceVersion].some(hasText);

export const hasAndroidPlatformData = (values = {}) =>
  [values.androidVersionName, values.androidBuildVersion, values.androidMinForceVersion].some(
    hasText,
  );

const optionalDottedVersion = (label) =>
  Yup.string()
    .trim()
    .test('dotted-version', `${label} must be a version like 1.2.3.5`, (value) => {
      if (value == null || value === '') return true;
      // Allow trailing dots while typing (e.g. "1.2.").
      const normalized = String(value).replace(/\.+$/, '');
      if (!normalized) return true;
      return VERSION_COMPLETE_PATTERN.test(normalized);
    });

const requiredWhenPlatformActive = (platformCheck, schema, requiredMessage) =>
  schema.test('platform-field-required', requiredMessage, function requiredWhenActive(value) {
    if (!platformCheck(this.parent)) return true;
    if (value === undefined || value === null || value === '') return false;
    return true;
  });

export const updateAppVersionSchema = Yup.object({
  userType: Yup.string()
    .required('Application type is required')
    .oneOf(APPLICATION_TYPES, 'Select a valid application type'),
  iosVersionName: requiredWhenPlatformActive(
    hasIosPlatformData,
    optionalDottedVersion('iOS Version Name').max(100, 'iOS Version Name is too long'),
    'iOS Version Name is required',
  ),
  iosBuildVersion: requiredWhenPlatformActive(
    hasIosPlatformData,
    optionalDottedVersion('iOS Build Version').max(100, 'iOS Build Version is too long'),
    'iOS Build Version is required',
  ),
  iosMinForceVersion: requiredWhenPlatformActive(
    hasIosPlatformData,
    optionalDottedVersion('iOS Min Force Version').max(100, 'iOS Min Force Version is too long'),
    'iOS Min Force Version is required',
  ),
  androidVersionName: requiredWhenPlatformActive(
    hasAndroidPlatformData,
    optionalDottedVersion('Android Version Name').max(100, 'Android Version Name is too long'),
    'Android Version Name is required',
  ),
  androidBuildVersion: requiredWhenPlatformActive(
    hasAndroidPlatformData,
    optionalDottedVersion('Android Build Version').max(100, 'Android Build Version is too long'),
    'Android Build Version is required',
  ),
  androidMinForceVersion: requiredWhenPlatformActive(
    hasAndroidPlatformData,
    optionalDottedVersion('Android Min Force Version').max(
      100,
      'Android Min Force Version is too long',
    ),
    'Android Min Force Version is required',
  ),
  message: requiredString('Message').trim().max(500, 'Message must be at most 500 characters'),
}).test({
  name: 'at-least-one-platform',
  test(values) {
    if (hasIosPlatformData(values) || hasAndroidPlatformData(values)) return true;
    return this.createError({
      path: 'platform',
      message: 'Add iOS or Android version details',
    });
  },
});

export { VERSION_COMPLETE_PATTERN };

export default updateAppVersionSchema;
